import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Portal from "@mui/material/Portal";
import CloseIcon from "@mui/icons-material/Close";
import BackdropLoadingScreen from "../../../common/BackdropLoadingScreen";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import {
  buildBulkProductChangePayload,
  buildMetricBulkUpdateApiPayload,
  createInitialProductBulkFormState,
  mergeImplicitExclusiveGroupDisablesIntoBulkPayload,
  findMissingSensitivityIds,
  getBulkPeerEnableBlockedExplanation,
  getMutuallyExclusiveBulkGroupForKey,
  isBulkRowRestrictedByPeerEnableSelection,
  ProductBulkFormRow,
  ProductBulkFormState,
  PRODUCT_BULK_ROWS,
  validateLoiteringSeconds,
  validateMutuallyExclusiveProductGroupsForCameras,
} from "./BulkUpdateWizardUtils";
import {
  fetchGeneralInfoSensitivityMap,
  fetchMetricSensitivityMap,
  fetchStreamProductsForCamera,
  fetchStreamsSimple,
  mergeSensitivityMaps,
  postMetricBulkUpdate,
  postStreamBulkUpdate,
} from "./SiteAboutPageUtils/bulkUpdateCamerasApi";
import {
  mergeBulkSlicingRows,
  partitionStreamIdsForSliceUpdate,
  buildStreamIdToProductLabelMap,
  type BulkSlicingMergedRow,
} from "./SiteAboutPageUtils/bulkSlicingRows";
import { pollMetricSimplifiedUntilBulkExpectationsMet } from "./SiteAboutPageUtils/metricSimplifiedPoll";

interface SelectedCamera {
  id: string | number;
  camera_name: string;
}

interface BulkUpdateProductsDialogProps {
  open: boolean;
  selectedCameras: SelectedCamera[];
  activeCameraProducts: { [cameraId: number]: { [productName: string]: boolean } };
  activeCameraProductsLoading: { [cameraId: number]: true };
  siteInfo: { id?: number };
  navigate: NavigateFunction;
  onClose: () => void;
  onApply: () => void;
  productRows?: ProductBulkFormRow[];
}

const STEP_LABELS = ["Update Type", "Configure", "Review"];

const buildProductNameMap = (rows: ProductBulkFormRow[]) =>
  rows.reduce(
    (accumulator, row) => {
      accumulator[row.key] = row.label;
      return accumulator;
    },
    {} as Record<string, string>
  );

const formatEnableValue = (value?: "enable" | "disable") => {
  if (value === "enable") return "Enable";
  if (value === "disable") return "Disable";
  return "—";
};

const formatSensitivityValue = (
  value?: "low" | "medium" | "medium_high" | "high"
) => {
  if (value === "low") return "Low";
  if (value === "medium") return "Medium";
  if (value === "medium_high") return "Medium-High";
  if (value === "high") return "High";
  return "—";
};

export const BulkUpdateProductsDialog = ({
  open,
  selectedCameras,
  activeCameraProducts,
  activeCameraProductsLoading,
  siteInfo,
  navigate,
  onClose,
  onApply,
  productRows = PRODUCT_BULK_ROWS,
}: BulkUpdateProductsDialogProps) => {
  const clearBulkCameraCachesForCameras = useSiteAboutStore(
    (s) => s.clearBulkCameraCachesForCameras
  );
  const refreshActiveCameraProductsFromSimplifiedEndpoint = useSiteAboutStore(
    (s) => s.refreshActiveCameraProductsFromSimplifiedEndpoint
  );
  const productNameMap = React.useMemo(
    () => buildProductNameMap(productRows),
    [productRows]
  );
  const orangeTextButtonSx = {
    fontFamily: "Mulish",
    color: "#FF8400",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "#FFF3E6",
      color: "#E67600",
    },
  };
  const orangeContainedButtonSx = {
    fontFamily: "Mulish",
    backgroundColor: "#FF8400",
    color: "white",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "#E67600",
    },
  };

  const [step, setStep] = React.useState(0);
  const [selectedUpdateType, setSelectedUpdateType] = React.useState<
    "products" | "slicing"
  >("products");
  const [formState, setFormState] = React.useState<ProductBulkFormState>(() =>
    createInitialProductBulkFormState(productRows)
  );
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [slicingMergedRows, setSlicingMergedRows] = React.useState<BulkSlicingMergedRow[]>(
    []
  );
  const [slicingRowsLoading, setSlicingRowsLoading] = React.useState(false);
  const [slicingRowsError, setSlicingRowsError] = React.useState<string | null>(null);
  const [slicingDesiredByStreamId, setSlicingDesiredByStreamId] = React.useState<
    Record<number, boolean>
  >({});
  const [applyLoading, setApplyLoading] = React.useState(false);
  const [applyError, setApplyError] = React.useState<string | null>(null);
  const [bulkResultSnackbar, setBulkResultSnackbar] = React.useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const selectedCameraIds = React.useMemo(
    () => selectedCameras.map((camera) => Number(camera.id)),
    [selectedCameras]
  );
  const selectedCameraIdsKey = selectedCameraIds.join(",");

  const slicingMergedRowsRef = React.useRef(slicingMergedRows);
  React.useEffect(() => {
    slicingMergedRowsRef.current = slicingMergedRows;
  }, [slicingMergedRows]);

  const setAllSlicingDesired = (slice: boolean) => {
    setSlicingDesiredByStreamId((previous) => {
      const next = { ...previous };
      slicingMergedRowsRef.current.forEach((row) => {
        next[row.streamId] = slice;
      });
      return next;
    });
  };

  const setSlicingDesiredForStream = (streamId: number, slice: boolean) => {
    setSlicingDesiredByStreamId((previous) => ({
      ...previous,
      [streamId]: slice,
    }));
  };

  React.useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedUpdateType("products");
      setFormState(createInitialProductBulkFormState(productRows));
      setFieldErrors({});
      setApplyError(null);
      setApplyLoading(false);
      setSlicingMergedRows([]);
      setSlicingRowsLoading(false);
      setSlicingRowsError(null);
      setSlicingDesiredByStreamId({});
    } else {
      setBulkResultSnackbar({ open: false, message: "" });
    }
  }, [open, productRows]);

  React.useEffect(() => {
    if (!open || step !== 1 || selectedUpdateType !== "slicing") {
      return;
    }
    const customerId = siteInfo?.id;
    if (customerId === undefined || customerId === null) {
      setSlicingRowsError("Missing site id for stream lookup.");
      setSlicingMergedRows([]);
      setSlicingDesiredByStreamId({});
      return;
    }

    let cancelled = false;
    setSlicingRowsLoading(true);
    setSlicingRowsError(null);

    void (async () => {
      try {
        const [simpleRows, ...productLists] = await Promise.all([
          fetchStreamsSimple(navigate, customerId),
          ...selectedCameraIds.map((id) => fetchStreamProductsForCamera(navigate, id)),
        ]);
        if (cancelled) {
          return;
        }
        const productByCamera = new Map<number, (typeof productLists)[number]>();
        selectedCameraIds.forEach((id, index) => {
          productByCamera.set(id, productLists[index] ?? []);
        });
        const streamIdToLabel = buildStreamIdToProductLabelMap(productByCamera);
        const nameMap = new Map(
          selectedCameras.map((c) => [Number(c.id), c.camera_name] as const)
        );
        const merged = mergeBulkSlicingRows(
          simpleRows,
          streamIdToLabel,
          selectedCameraIds,
          nameMap
        );
        if (cancelled) {
          return;
        }
        setSlicingMergedRows(merged);
        setSlicingDesiredByStreamId(
          Object.fromEntries(merged.map((row) => [row.streamId, row.currentSlice]))
        );
      } catch {
        if (!cancelled) {
          setSlicingRowsError("Could not load slicing data. Try again.");
          setSlicingMergedRows([]);
          setSlicingDesiredByStreamId({});
        }
      } finally {
        if (!cancelled) {
          setSlicingRowsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    step,
    selectedUpdateType,
    siteInfo?.id,
    selectedCameraIdsKey,
    navigate,
    selectedCameras,
    selectedCameraIds,
  ]);

  React.useEffect(() => {
    if (!open || selectedUpdateType !== "products") {
      return;
    }
    setFormState((previousState) => {
      let next: ProductBulkFormState | null = null;
      const ensureNext = () => {
        if (!next) {
          next = { ...previousState };
        }
        return next;
      };
      productRows.forEach((row) => {
        if (!getMutuallyExclusiveBulkGroupForKey(row.key)) {
          return;
        }
        if (!isBulkRowRestrictedByPeerEnableSelection(row.key, previousState)) {
          return;
        }
        const rowState = previousState[row.key];
        if (!rowState) {
          return;
        }
        const loiterTrim =
          row.supportsLoiteringSeconds && rowState.loiteringSeconds
            ? rowState.loiteringSeconds.trim()
            : "";
        const needsClear =
          rowState.enable === "enable" ||
          rowState.sensitivity !== "no_change" ||
          loiterTrim.length > 0;
        if (!needsClear) {
          return;
        }
        const draft = ensureNext();
        draft[row.key] = {
          ...rowState,
          enable: rowState.enable === "enable" ? "no_change" : rowState.enable,
          sensitivity: "no_change",
          loiteringSeconds: row.supportsLoiteringSeconds ? "" : rowState.loiteringSeconds,
        };
      });
      return next ?? previousState;
    });
  }, [open, selectedUpdateType, formState, productRows]);
  const loadingCount = selectedCameraIds.filter(
    (cameraId) => activeCameraProductsLoading[cameraId]
  ).length;
  const payloadPreview = buildBulkProductChangePayload(productRows, formState);

  const setFieldState = (
    key: string,
    update: Partial<{
      enable: "no_change" | "enable" | "disable";
      sensitivity: "no_change" | "low" | "medium" | "medium_high" | "high";
      loiteringSeconds: string;
    }>
  ) => {
    setFormState((previousState) => ({
      ...previousState,
      [key]: {
        ...previousState[key],
        ...update,
      },
    }));
  };

  const handleNext = () => {
    if (step === 1 && selectedUpdateType === "products") {
      const errors = validateLoiteringSeconds(productRows, formState);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
      const exclusiveError = validateMutuallyExclusiveProductGroupsForCameras(
        selectedCameraIds,
        formState,
        activeCameraProducts
      );
      if (exclusiveError) {
        setApplyError(exclusiveError);
        return;
      }
      setApplyError(null);
    }
    if (step === 1 && selectedUpdateType === "slicing") {
      if (slicingRowsLoading) {
        return;
      }
      if (slicingRowsError) {
        return;
      }
      if (slicingMergedRows.length === 0) {
        setApplyError("No stream products found for the selected cameras.");
        return;
      }
      setApplyError(null);
    }
    setStep((previousStep) => Math.min(previousStep + 1, STEP_LABELS.length - 1));
  };

  const handleApply = async () => {
    setApplyError(null);
    if (selectedUpdateType === "products") {
      const errors = validateLoiteringSeconds(productRows, formState);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        setStep(1);
        return;
      }
      if (loadingCount > 0) {
        setApplyError("Still loading product status for some cameras. Please wait.");
        return;
      }
      const exclusiveError = validateMutuallyExclusiveProductGroupsForCameras(
        selectedCameraIds,
        formState,
        activeCameraProducts
      );
      if (exclusiveError) {
        setApplyError(exclusiveError);
        setStep(1);
        return;
      }
    }

    setApplyLoading(true);
    try {
      if (selectedUpdateType === "products") {
        const referenceCameraId = selectedCameraIds[0];
        let sensitivityMap = await fetchMetricSensitivityMap(
          navigate,
          referenceCameraId
        );
        let missingLabels = findMissingSensitivityIds(
          productRows,
          formState,
          sensitivityMap
        );
        if (missingLabels.length > 0) {
          // /metric/?camera_id= only lists metrics that exist — disabling a
          // product hard-deletes its row — so enabling a currently-disabled
          // product with a sensitivity can't resolve from it (SUP-287).
          // general_info includes options for every available product (the
          // same source Edit Camera uses), so fill the gaps from there.
          try {
            const generalInfoMap = await fetchGeneralInfoSensitivityMap(
              navigate,
              referenceCameraId
            );
            sensitivityMap = mergeSensitivityMaps(sensitivityMap, generalInfoMap);
            missingLabels = findMissingSensitivityIds(
              productRows,
              formState,
              sensitivityMap
            );
          } catch {
            // Fall through to the unresolved-sensitivity error below.
          }
        }
        if (missingLabels.length > 0) {
          setApplyError(
            `Could not resolve sensitivity options for: ${missingLabels.join(
              ", "
            )}. Try again or update those products from Edit Camera.`
          );
          return;
        }
        const bulkItems = mergeImplicitExclusiveGroupDisablesIntoBulkPayload(
          formState,
          buildMetricBulkUpdateApiPayload(
            productRows,
            formState,
            sensitivityMap
          )
        );
        if (bulkItems.length === 0) {
          setApplyError("No product changes to apply. Adjust the form or choose No Change.");
          return;
        }
        const mergeSnapshot: { [id: number]: Record<string, boolean> } = {};
        selectedCameraIds.forEach((id) => {
          mergeSnapshot[id] = { ...(activeCameraProducts[id] ?? {}) };
        });
        const data = await postMetricBulkUpdate(
          navigate,
          selectedCameraIds,
          bulkItems
        );
        clearBulkCameraCachesForCameras(selectedCameraIds);
        // Product changes ride settings.json to the connector — mark the site
        // unsynced so the Sync Site button turns red and directs the operator
        // to sync (then restart) rather than leaving the change silent.
        markSiteUnsynced();
        const queued = data.cameras_queued ?? selectedCameraIds.length;
        const customerId = siteInfo?.id;

        if (data.status === "queued" && customerId !== undefined && customerId !== null) {
          const matched = await pollMetricSimplifiedUntilBulkExpectationsMet(
            navigate,
            customerId,
            selectedCameraIds,
            formState,
            mergeSnapshot,
            { productRows }
          );
          if (matched) {
            await refreshActiveCameraProductsFromSimplifiedEndpoint(
              navigate,
              customerId,
              selectedCameraIds
            );
            setBulkResultSnackbar({
              open: true,
              message: `Bulk product update completed for ${queued} camera(s).`,
            });
          } else {
            setBulkResultSnackbar({
              open: true,
              message: `Bulk product update has been queued for ${queued} camera(s). Updates may take some time to complete.`,
            });
          }
        } else if (data.status === "queued") {
          setBulkResultSnackbar({
            open: true,
            message: `Bulk product update has been queued for ${queued} camera(s). Updates may take some time to complete.`,
          });
        } else {
          setBulkResultSnackbar({
            open: true,
            message: `Bulk product update submitted for ${queued} camera(s).`,
          });
        }
        onApply();
        return;
      }

      const { enableIds, disableIds } = partitionStreamIdsForSliceUpdate(
        slicingMergedRows,
        slicingDesiredByStreamId
      );
      if (enableIds.length === 0 && disableIds.length === 0) {
        setApplyError("No slice changes to apply.");
        return;
      }
      if (enableIds.length > 0) {
        await postStreamBulkUpdate(navigate, {
          stream_ids: enableIds,
          slice: true,
        });
      }
      if (disableIds.length > 0) {
        await postStreamBulkUpdate(navigate, {
          stream_ids: disableIds,
          slice: false,
        });
      }
      clearBulkCameraCachesForCameras(selectedCameraIds);
      // Slicing also rides settings.json — same unsynced marking as products.
      markSiteUnsynced();
      setBulkResultSnackbar({
        open: true,
        message: "Slicing has been updated for the selected cameras.",
      });
      onApply();
    } catch (reason) {
      universalAPIErrorHandler(reason as AxiosError, navigate, () => {}, () => {});
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <>
      <BackdropLoadingScreen
        openState={applyLoading}
        stackAboveModal
        message="Applying bulk update…"
      />
      <Dialog
      open={open}
      onClose={() => {
        if (applyLoading) {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown={applyLoading}
      maxWidth={
        (step === 1 &&
          (selectedUpdateType === "products" || selectedUpdateType === "slicing")) ||
        (step === 2 && selectedUpdateType === "slicing")
          ? "lg"
          : "sm"
      }
      fullWidth
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          fontWeight: 700,
          bgcolor: "#1B2A4A",
          color: "white",
          py: 2.25,
          pr: 6,
        }}
      >
        Bulk Update
        <IconButton
          onClick={onClose}
          disabled={applyLoading}
          aria-label="close bulk update dialog"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        {applyError ? (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: "Mulish" }}
            onClose={() => setApplyError(null)}
          >
            {applyError}
          </Alert>
        ) : null}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            {STEP_LABELS.map((label, index) => (
              <React.Fragment key={label}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Mulish",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: index <= step ? "white" : "#9E9E9E",
                      bgcolor: index <= step ? "#E8871E" : "#EEEEEE",
                    }}
                  >
                    {index < step ? "✓" : index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Mulish",
                      fontSize: "13px",
                      fontWeight: index === step ? 700 : 600,
                      color: index === step ? "#212121" : "#757575",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
                {index < STEP_LABELS.length - 1 && (
                  <Box sx={{ width: 18, height: 2, bgcolor: "#E0E0E0" }} />
                )}
              </React.Fragment>
            ))}
          </Box>
          <Typography sx={{ fontFamily: "Mulish", fontSize: "12px", color: "#757575" }}>
            Applying to {selectedCameras.length} camera
            {selectedCameras.length === 1 ? "" : "s"}
          </Typography>
        </Box>

        {step === 0 && (
          <Box>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1 }}>
              Update Type
            </Typography>
            <Typography sx={{ fontFamily: "Mulish", mb: 2, color: "#616161" }}>
              Choose what to bulk update.
            </Typography>
            <Box
              sx={{
                border: "1px solid #E8871E",
                borderRadius: 2,
                bgcolor:
                  selectedUpdateType === "products" ? "#FFF3E6" : "transparent",
                p: 1.5,
                mb: 2,
                cursor: "pointer",
              }}
              onClick={() => setSelectedUpdateType("products")}
            >
              <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, color: "#E8871E" }}>
                Products
              </Typography>
              <Typography sx={{ fontFamily: "Mulish", fontSize: "13px", color: "#616161" }}>
                Enable/Disable products, update sensitivity, and optionally set loitering times.
              </Typography>
            </Box>
            <Box
              sx={{
                border: "1px solid #E8871E",
                borderRadius: 2,
                bgcolor:
                  selectedUpdateType === "slicing" ? "#FFF3E6" : "transparent",
                p: 1.5,
                mb: 2,
                cursor: "pointer",
              }}
              onClick={() => setSelectedUpdateType("slicing")}
            >
              <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, color: "#E8871E" }}>
                Slicing
              </Typography>
              <Typography sx={{ fontFamily: "Mulish", fontSize: "13px", color: "#616161" }}>
                Enable or disable slicing per stream product for the selected cameras.
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1 }}>
              Selected Cameras ({selectedCameras.length})
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {selectedCameras.map((camera) => (
                <Box
                  key={camera.id}
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 99,
                    fontSize: "12px",
                    color: "#E8871E",
                    backgroundColor: "#FFF3E6",
                    fontFamily: "Mulish",
                    fontWeight: 700,
                  }}
                >
                  {camera.camera_name}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {step === 1 && selectedUpdateType === "products" && (
          <Box>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1 }}>
              Current Products
            </Typography>
            

            <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Camera</TableCell>
                    {productRows.map((row) => (
                      <TableCell key={row.key} sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
                        {row.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCameras.map((camera) => {
                    const cameraId = Number(camera.id);
                    const loading = activeCameraProductsLoading[cameraId];
                    const productStates = activeCameraProducts[cameraId] || {};

                    return (
                      <TableRow key={camera.id}>
                        <TableCell sx={{ fontFamily: "Mulish" }}>{camera.camera_name}</TableCell>
                        {productRows.map((row) => {
                          if (loading) {
                            return (
                              <TableCell key={row.key}>
                                <CircularProgress size={14} />
                              </TableCell>
                            );
                          }
                          const enabled =
                            productStates[productNameMap[row.key]] === true ||
                            productStates[row.label] === true;
                          return (
                            <TableCell key={row.key}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  bgcolor: enabled ? "#2E8B3C" : "#D94040",
                                  mx: "auto",
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1, paddingBottom: 1 }}>
              Updated Products
            </Typography>
            <Typography sx={{ fontFamily: "Mulish", mb: 2, color: "#616161" }}>Configure No Change, Enable, Disable, sensitivity, and optional loitering time fields.</Typography>
            <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Enable</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
                      Sensitivity
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
                      Loitering Time (sec)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productRows.map((row) => {
                    const peerRestricted = isBulkRowRestrictedByPeerEnableSelection(
                      row.key,
                      formState
                    );
                    return (
                      <TableRow key={row.key}>
                        <TableCell sx={{ fontFamily: "Mulish" }}>{row.label}</TableCell>
                        {peerRestricted ? (
                          <TableCell colSpan={3} sx={{ maxWidth: 520, verticalAlign: "top" }}>
                            <Typography
                              sx={{
                                fontFamily: "Mulish",
                                fontSize: "12px",
                                lineHeight: 1.45,
                                color: "#616161",
                              }}
                            >
                              {getBulkPeerEnableBlockedExplanation(row.key, formState)}
                            </Typography>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell sx={{ verticalAlign: "top" }}>
                              <TextField
                                select
                                size="small"
                                SelectProps={{ native: true }}
                                value={formState[row.key].enable}
                                inputProps={{ "data-testid": `bulk-enable-${row.key}` }}
                                onChange={(event) =>
                                  setFieldState(row.key, {
                                    enable: event.target.value as
                                      | "no_change"
                                      | "enable"
                                      | "disable",
                                  })
                                }
                                sx={{ minWidth: 130 }}
                              >
                                <option value="no_change">No Change</option>
                                <option value="enable">Enable All</option>
                                <option value="disable">Disable All</option>
                              </TextField>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top" }}>
                              {row.supportsSensitivity ? (
                                <TextField
                                  select
                                  size="small"
                                  SelectProps={{ native: true }}
                                  value={formState[row.key].sensitivity}
                                  inputProps={{
                                    "data-testid": `bulk-sensitivity-${row.key}`,
                                  }}
                                  onChange={(event) =>
                                    setFieldState(row.key, {
                                      sensitivity: event.target.value as
                                        | "no_change"
                                        | "low"
                                        | "medium"
                                        | "medium_high"
                                        | "high",
                                    })
                                  }
                                  sx={{ minWidth: 130 }}
                                >
                                  <option value="no_change">No Change</option>
                                  <option value="low">Low</option>
                                  <option value="medium">Medium</option>
                                  <option value="medium_high">Medium-High</option>
                                  <option value="high">High</option>
                                </TextField>
                              ) : (
                                <Typography sx={{ fontFamily: "Mulish", color: "#9E9E9E" }}>
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top" }}>
                              {row.supportsLoiteringSeconds ? (
                                <TextField
                                  size="small"
                                  value={formState[row.key].loiteringSeconds}
                                  onChange={(event) =>
                                    setFieldState(row.key, {
                                      loiteringSeconds: event.target.value,
                                    })
                                  }
                                  error={Boolean(fieldErrors[row.key])}
                                  helperText={fieldErrors[row.key] ?? "Optional"}
                                  inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    "data-testid": `bulk-loitering-${row.key}`,
                                  }}
                                />
                              ) : (
                                <Typography sx={{ fontFamily: "Mulish", color: "#9E9E9E" }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {loadingCount > 0 && (
              <Typography sx={{ fontFamily: "Mulish", mt: 1, color: "#616161" }}>
                Loading camera product status for {loadingCount} selected camera
                {loadingCount === 1 ? "" : "s"}...
              </Typography>
            )}
          </Box>
        )}

        {step === 1 && selectedUpdateType === "slicing" && (
          <Box>
            {slicingRowsError ? (
              <Alert severity="error" sx={{ mb: 2, fontFamily: "Mulish" }}>
                {slicingRowsError}
              </Alert>
            ) : null}
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1 }}>
              Current Slicing
            </Typography>
            <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Camera</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Products</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Slicing</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slicingRowsLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontFamily: "Mulish" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                          <CircularProgress size={18} />
                          Loading stream products and slicing status…
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : slicingMergedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontFamily: "Mulish", color: "#616161" }}>
                        No stream products found for the selected cameras.
                      </TableCell>
                    </TableRow>
                  ) : (
                    slicingMergedRows.map((row) => (
                      <TableRow key={row.streamId}>
                        <TableCell sx={{ fontFamily: "Mulish" }}>
                          {row.cameraName || `Camera ${row.cameraId}`}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Mulish" }}>{row.productLabel}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: row.currentSlice ? "#2E8B3C" : "#D94040",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1, paddingBottom: 1 }}>
              Updated Slicing
            </Typography>
            <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Camera</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Products</TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }} align="center">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Typography
                          sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: "12px" }}
                        >
                          Enable
                        </Typography>
                        <Checkbox
                          size="small"
                          disabled={slicingRowsLoading || slicingMergedRows.length === 0}
                          checked={
                            slicingMergedRows.length > 0 &&
                            slicingMergedRows.every(
                              (r) => slicingDesiredByStreamId[r.streamId] === true
                            )
                          }
                          indeterminate={
                            slicingMergedRows.some(
                              (r) => slicingDesiredByStreamId[r.streamId] === true
                            ) &&
                            !slicingMergedRows.every(
                              (r) => slicingDesiredByStreamId[r.streamId] === true
                            )
                          }
                          onChange={() => setAllSlicingDesired(true)}
                          inputProps={{ "aria-label": "Select enable slicing for all rows" }}
                          data-testid="bulk-slicing-select-all-enable"
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }} align="center">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Typography
                          sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: "12px" }}
                        >
                          Disable
                        </Typography>
                        <Checkbox
                          size="small"
                          disabled={slicingRowsLoading || slicingMergedRows.length === 0}
                          checked={
                            slicingMergedRows.length > 0 &&
                            slicingMergedRows.every(
                              (r) => slicingDesiredByStreamId[r.streamId] === false
                            )
                          }
                          indeterminate={
                            slicingMergedRows.some(
                              (r) => slicingDesiredByStreamId[r.streamId] === false
                            ) &&
                            !slicingMergedRows.every(
                              (r) => slicingDesiredByStreamId[r.streamId] === false
                            )
                          }
                          onChange={() => setAllSlicingDesired(false)}
                          inputProps={{ "aria-label": "Select disable slicing for all rows" }}
                          data-testid="bulk-slicing-select-all-disable"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slicingRowsLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontFamily: "Mulish" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                          <CircularProgress size={18} />
                          Loading…
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : slicingMergedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ fontFamily: "Mulish", color: "#616161" }}>
                        No rows to update.
                      </TableCell>
                    </TableRow>
                  ) : (
                    slicingMergedRows.map((row) => {
                      const desired = slicingDesiredByStreamId[row.streamId];
                      const enableChecked = desired === true;
                      const disableChecked = desired === false;
                      return (
                        <TableRow key={`edit-${row.streamId}`}>
                          <TableCell sx={{ fontFamily: "Mulish" }}>
                            {row.cameraName || `Camera ${row.cameraId}`}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish" }}>{row.productLabel}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={enableChecked}
                              onChange={() => setSlicingDesiredForStream(row.streamId, true)}
                              inputProps={{
                                "aria-label": `Enable slicing for ${row.productLabel}`,
                              }}
                              data-testid={`bulk-slicing-enable-${row.streamId}`}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={disableChecked}
                              onChange={() => setSlicingDesiredForStream(row.streamId, false)}
                              inputProps={{
                                "aria-label": `Disable slicing for ${row.productLabel}`,
                              }}
                              data-testid={`bulk-slicing-disable-${row.streamId}`}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, mb: 1 }}>
              Review Changes
            </Typography>
            {selectedUpdateType === "products" && payloadPreview.length === 0 ? (
              <Typography sx={{ fontFamily: "Mulish", color: "#9E9E9E" }}>
                No changes selected yet.
              </Typography>
            ) : null}
            {selectedUpdateType === "products" && payloadPreview.length > 0 && (
              <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Product</TableCell>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Enable/Disable</TableCell>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
                        Sensitivity
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
                        Loitering (sec)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payloadPreview.map((item) => (
                      <TableRow key={item.product}>
                        <TableCell sx={{ fontFamily: "Mulish" }}>
                          {productNameMap[item.product] ?? item.product}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Mulish" }}>
                          {formatEnableValue(item.enable)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Mulish" }}>
                          {formatSensitivityValue(item.sensitivity)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "Mulish" }}>
                          {item.loitering_seconds ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {selectedUpdateType === "slicing" && (
              <TableContainer sx={{ border: "1px solid #E0E0E0", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Camera</TableCell>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Products</TableCell>
                      <TableCell sx={{ fontFamily: "Mulish", fontWeight: 700 }}>Slicing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slicingMergedRows.map((row) => {
                      const proposed = slicingDesiredByStreamId[row.streamId] === true;
                      return (
                        <TableRow key={`review-${row.streamId}`}>
                          <TableCell sx={{ fontFamily: "Mulish" }}>
                            {row.cameraName || `Camera ${row.cameraId}`}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "Mulish" }}>{row.productLabel}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: proposed ? "#2E8B3C" : "#D94040",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {selectedUpdateType === "products" ? (
              <Box
                sx={{
                  mt: 2,
                  border: "1px solid #FFE082",
                  backgroundColor: "#FFF8E1",
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontFamily: "Mulish", fontSize: "13px", color: "#8D6E00" }}>
                     Only products set to Enable or Disable will change. Products left as No Change keep current settings.
                </Typography>
              </Box>
              ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step > 0 && (
          <Button
            onClick={() => setStep((previousStep) => previousStep - 1)}
            sx={orangeTextButtonSx}
          >
            Back
          </Button>
        )}
        {step < 2 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              applyLoading ||
              (step === 1 &&
                selectedUpdateType === "slicing" &&
                (slicingRowsLoading ||
                  Boolean(slicingRowsError) ||
                  slicingMergedRows.length === 0))
            }
            sx={orangeContainedButtonSx}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={() => void handleApply()}
            disabled={applyLoading}
            sx={orangeContainedButtonSx}
          >
            {applyLoading ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : (
              "Apply Update"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
      <Portal>
        <Snackbar
          open={bulkResultSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setBulkResultSnackbar({ open: false, message: "" })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity="success"
            onClose={() => setBulkResultSnackbar({ open: false, message: "" })}
            sx={{ width: "100%", fontFamily: "Mulish" }}
          >
            {bulkResultSnackbar.message}
          </MuiAlert>
        </Snackbar>
      </Portal>
    </>
  );
};
